import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import {
  createA3Pedidos,
  getA3Proveedores,
} from "../../api/Produccion/a3ArticulosApi";
import {
  buildImportA3PedidoPayload,
  IMPORT_A3_CODART,
  matchA3CodproByProveedorNombre,
} from "../../utils/importA3PedidoPure";

function fmtMoney(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return x.toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function a3ProveedorCodigo(p) {
  return String(p?.CODPRO ?? p?.codpro ?? "").trim();
}

function a3ProveedorNombre(p) {
  return String(
    p?.NOMBRE ??
      p?.nombre ??
      p?.NOMPRO ??
      p?.nompro ??
      p?.RAZON ??
      p?.razon ??
      "",
  ).trim();
}

function proveedorLabel(p) {
  if (!p) return "";
  const cod = a3ProveedorCodigo(p);
  const nom = a3ProveedorNombre(p);
  if (cod && nom) return `${cod} — ${nom}`;
  return nom || cod;
}

const filterProveedores = createFilterOptions({
  stringify: (option) =>
    `${a3ProveedorCodigo(option)} ${a3ProveedorNombre(option)}`,
  limit: 100,
});

/**
 * Tras crear el PO de prenda: preview y alta de PO compra-only import (CODART 0).
 */
export default function EstimateImportA3PedidoDialog({
  open,
  onClose,
  groups = [],
  referenciaInicial = "",
  dbSource = "STAGE_DESIGN",
  idPresupuesto,
  idProyecto,
  fecha = null,
  centroCosteEmpresa = "",
  centroCosteMarca = "",
  centroCosteDepartamento = "",
  onCreated,
}) {
  const [referencia, setReferencia] = useState(referenciaInicial || "");
  const [proveedores, setProveedores] = useState([]);
  const [loadingProv, setLoadingProv] = useState(false);
  const [codproByGroup, setCodproByGroup] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const safeGroups = useMemo(
    () =>
      (Array.isArray(groups) ? groups : []).filter(
        (g) => g?.lineasConcepto?.length,
      ),
    [groups],
  );

  const totalAll = useMemo(
    () => safeGroups.reduce((acc, g) => acc + Number(g.total || 0), 0),
    [safeGroups],
  );

  const sortedProveedores = useMemo(() => {
    return [...proveedores].sort((a, b) =>
      proveedorLabel(a).localeCompare(proveedorLabel(b), undefined, {
        sensitivity: "base",
      }),
    );
  }, [proveedores]);

  useEffect(() => {
    if (!open) return;
    setReferencia(referenciaInicial || "");
    setError("");
    const initial = {};
    for (const g of safeGroups) {
      initial[g.proveedorKey] = "";
    }
    setCodproByGroup(initial);
  }, [open, referenciaInicial, safeGroups]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingProv(true);
      try {
        const rows = await getA3Proveedores("", dbSource, 2000);
        if (cancelled) return;
        const list = Array.isArray(rows?.rows)
          ? rows.rows
          : Array.isArray(rows)
            ? rows
            : [];
        setProveedores(list);
        setCodproByGroup((prev) => {
          const next = { ...prev };
          for (const g of safeGroups) {
            if (String(next[g.proveedorKey] || "").trim()) continue;
            const matched = matchA3CodproByProveedorNombre(
              g.proveedorNombre,
              list,
              g.codproEscandallo,
            );
            if (matched) next[g.proveedorKey] = matched;
          }
          return next;
        });
      } catch (e) {
        if (!cancelled) {
          setProveedores([]);
          setError(
            e?.response?.data?.error ||
              e?.message ||
              "No se pudieron cargar proveedores A3",
          );
        }
      } finally {
        if (!cancelled) setLoadingProv(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, dbSource, safeGroups]);

  const findProv = (cod) => {
    const c = String(cod || "").trim();
    if (!c) return null;
    return sortedProveedores.find((p) => a3ProveedorCodigo(p) === c) || null;
  };

  const handleConfirm = async () => {
    const ref = String(referencia || "").trim();
    if (!ref) {
      setError("La referencia es obligatoria");
      return;
    }
    for (const g of safeGroups) {
      const cod = String(codproByGroup[g.proveedorKey] || "").trim();
      if (!cod || !findProv(cod)) {
        setError(
          "Selecciona un proveedor A3 de la lista (no uses el CODPROV Matrix)",
        );
        return;
      }
    }

    setSubmitting(true);
    setError("");
    try {
      const results = [];
      for (let i = 0; i < safeGroups.length; i += 1) {
        const g = safeGroups[i];
        const codpro = String(codproByGroup[g.proveedorKey] || "").trim();
        const selected = findProv(codpro);
        const refGroup =
          safeGroups.length === 1 ? ref : `${ref}${i === 0 ? "" : `-${i + 1}`}`;
        const payload = buildImportA3PedidoPayload({
          group: { ...g, codpro },
          referencia: refGroup,
          dbSource,
          idPresupuesto,
          idProyecto,
          fecha,
          centroCosteEmpresa,
          centroCosteMarca,
          centroCosteDepartamento,
        });
        payload.proveedorNombre = a3ProveedorNombre(selected);
        const resultado = await createA3Pedidos(payload);
        results.push({ payload, resultado });
      }
      onCreated?.(results);
      onClose?.();
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e?.message ||
          "Error al crear el pedido de import en A3",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>¿Crear pedido A3 de import?</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            El pedido de prenda ya está creado. Puedes generar un segundo pedido
            de compra (CODART {IMPORT_A3_CODART}) con los conceptos IMPORT del
            escandallo, sin modificar el coste de la prenda.
          </Typography>

          <TextField
            size="small"
            label="Referencia"
            value={referencia}
            onChange={(e) => setReferencia(e.target.value)}
            fullWidth
            disabled={submitting}
          />

          {safeGroups.map((g) => {
            const selectedCod = String(
              codproByGroup[g.proveedorKey] || "",
            ).trim();
            const selected = findProv(selectedCod);
            return (
              <Box
                key={g.proveedorKey}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1.5,
                }}
              >
                <Autocomplete
                  size="small"
                  options={sortedProveedores}
                  loading={loadingProv}
                  value={selected}
                  onChange={(_, option) => {
                    setCodproByGroup((prev) => ({
                      ...prev,
                      [g.proveedorKey]: a3ProveedorCodigo(option),
                    }));
                    setError("");
                  }}
                  filterOptions={filterProveedores}
                  getOptionLabel={proveedorLabel}
                  isOptionEqualToValue={(a, b) =>
                    a3ProveedorCodigo(a) === a3ProveedorCodigo(b)
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={a3ProveedorCodigo(option)}>
                      {proveedorLabel(option)}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Proveedor A3"
                      placeholder="Buscar por nombre o código A3…"
                      required
                      error={Boolean(error) && !selected}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingProv ? (
                              <CircularProgress color="inherit" size={16} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  disabled={submitting || loadingProv}
                  sx={{ mb: 1.5 }}
                />
                {(g.proveedorNombre || g.codproEscandallo) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1 }}
                  >
                    Escandallo Matrix: {g.proveedorNombre || "—"}
                    {g.codproEscandallo
                      ? ` (CODPROV ${g.codproEscandallo})`
                      : ""}
                    . Elige el transitario correspondiente en A3.
                  </Typography>
                )}

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Concepto</TableCell>
                      <TableCell align="right">Importe</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(g.lineasConcepto || []).map((l, idx) => (
                      <TableRow key={`${l.desclin}-${idx}`}>
                        <TableCell>{l.desclin}</TableCell>
                        <TableCell align="right">
                          {fmtMoney(l.precio)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell>
                        <strong>Total grupo</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{fmtMoney(g.total)}</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            );
          })}

          {safeGroups.length > 1 ? (
            <Typography variant="subtitle2">
              Total import: {fmtMoney(totalAll)}
            </Typography>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          No, solo prenda
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting || !safeGroups.length || loadingProv}
        >
          {submitting ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Crear pedido import"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
